import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Home,
  Users,
  CalendarPlus,
  Calendar,
  Building,
  FileText,
  User,
  List,
  Settings,
  Trash2,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Bell,
  ShoppingBag,
  Check,
  Repeat,
  CalendarOff,
  BarChart3,
  Clock,
  CalendarCheck
} from "lucide-react";
import { format, parseISO } from "date-fns";

const Layout = () => {
  const { user, canManage, isAdmin, logout, getAuthHeaders, unreadNotifications, refreshNotifications } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: getAuthHeaders(),
        
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchNotifications();
      refreshNotifications();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(
        `${API}/notifications/read-all`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchNotifications();
      refreshNotifications();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case "open_shift":
        return <Calendar className="w-4 h-4 text-[#E55934]" />;
      case "trade_request":
        return <Users className="w-4 h-4 text-[#6A1E2E]" />;
      case "drop_request":
        return <FileText className="w-4 h-4 text-[#B8860B]" />;
      case "marketplace":
        return <ShoppingBag className="w-4 h-4 text-[#6A1E2E]" />;
      default:
        return <Bell className="w-4 h-4 text-[#666666]" />;
    }
  };

  const navItems = [
    { icon: Home, label: "Dashboard", path: "/dashboard", show: true },
    { icon: Users, label: "Add Employee", path: "/add-employee", show: canManage },
    { icon: CalendarPlus, label: "Add Event", path: "/add-event", show: canManage },
    { icon: Repeat, label: "Recurring Shifts", path: "/recurring-shifts", show: canManage },
    { icon: Calendar, label: "Work Schedule", path: "/work-schedule", show: true },
    { icon: Building, label: "Company Schedule", path: "/company-schedule", show: true },
    { icon: ShoppingBag, label: "Marketplace", path: "/marketplace", show: true },
    { icon: CalendarCheck, label: "Open Shifts", path: "/open-shifts", show: true },
    { icon: CalendarOff, label: "Time Off", path: "/time-off", show: true },
    { icon: Clock, label: "Availability", path: "/availability", show: true },
    { icon: FileText, label: "Reports", path: "/reports", show: canManage },
    { icon: BarChart3, label: "Analytics", path: "/analytics", show: canManage },
    { icon: User, label: "Info", path: "/info", show: true },
    { icon: List, label: "Employee List", path: "/employee-list", show: true },
    { icon: Settings, label: "Settings", path: "/settings", show: isAdmin },
    { icon: Trash2, label: "Recently Deleted", path: "/recently-deleted", show: isAdmin },
  ];

  const visibleNavItems = navItems.filter((item) => item.show);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="main-layout">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <NavLink to="/dashboard" className="flex items-center gap-2">
                <span className="text-2xl font-bold font-heading text-[#6A1E2E]">OSMC</span>
                <span className="text-sm text-[#666666] hidden sm:inline">Work App</span>
              </NavLink>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium transition-colors rounded-sm ${
                      isActive
                        ? "text-[#6A1E2E] bg-[#F0EAEB]"
                        : "text-[#666666] hover:text-[#1A1A1A] hover:bg-[#FAFAFA]"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* Right side: Notifications & User Menu */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setShowNotifications(true)}
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5 text-[#666666]" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E55934] text-white text-xs rounded-full flex items-center justify-center">
                    {unreadNotifications > 9 ? "9+" : unreadNotifications}
                  </span>
                )}
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-[#1A1A1A] hover:bg-[#F0EAEB]"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#6A1E2E] flex items-center justify-center text-white text-sm font-semibold">
                      {user?.first_name?.[0] || "U"}
                    </div>
                    <span className="hidden sm:inline text-sm">
                      {user?.first_name} {user?.last_name}
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#999999]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b border-[#E5E5E5]">
                    <p className="text-sm font-medium text-[#1A1A1A]">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-[#666666]">{user?.email}</p>
                    <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-sm ${
                      user?.role === "admin" 
                        ? "text-[#6A1E2E] bg-[#F0EAEB]"
                        : user?.role === "supervisor"
                        ? "text-[#E55934] bg-orange-50"
                        : "text-[#666666] bg-[#FAFAFA]"
                    }`}>
                      {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) || "Employee"}
                    </span>
                  </div>
                  <DropdownMenuItem onClick={() => navigate("/info")} data-testid="menu-my-info">
                    <User className="w-4 h-4 mr-2" />
                    My Info
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-[#E5E5E5] bg-white" data-testid="mobile-nav">
            <div className="px-4 py-2 space-y-1">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                      isActive
                        ? "text-[#6A1E2E] bg-[#F0EAEB]"
                        : "text-[#666666] hover:text-[#1A1A1A] hover:bg-[#FAFAFA]"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Notifications Dialog */}
      <Dialog open={showNotifications} onOpenChange={setShowNotifications}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#6A1E2E]" />
                Notifications
              </span>
              {notifications.some((n) => !n.is_read) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-[#6A1E2E]"
                >
                  Mark all as read
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-96">
            {loadingNotifications ? (
              <div className="text-center py-8 text-[#666666]">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-[#999999]">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No notifications
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`p-3 rounded-sm border transition-colors cursor-pointer ${
                      notif.is_read
                        ? "bg-white border-[#E5E5E5]"
                        : "bg-[#F0EAEB] border-[#6A1E2E]/20"
                    }`}
                    onClick={() => !notif.is_read && markAsRead(notif.id)}
                    data-testid={`notification-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">{getNotificationIcon(notif.type)}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A1A1A]">{notif.title}</p>
                        <p className="text-xs text-[#666666] mt-1">{notif.message}</p>
                        <p className="text-xs text-[#999999] mt-1">
                          {format(parseISO(notif.created_at), "MMM d, h:mm a")}
                        </p>
                      </div>
                      {!notif.is_read && (
                        <div className="w-2 h-2 rounded-full bg-[#6A1E2E] mt-2"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
